import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Link, NavLink } from "react-router";

import { cn } from "@/lib/utils";

const itemClass =
  "flex min-w-0 items-center gap-2.5 rounded-md px-2.5 outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/40 [&_svg]:size-3.5 [&_svg]:shrink-0";

/** A 6px dot before the label that turns pink on the active page (links without an icon). */
const indicatorClass = "before:size-1.5 before:shrink-0 before:rounded-full before:content-['']";

interface SidebarLinkProps {
  to: string;
  children: ReactNode;
  /** Called after the link is clicked (the mobile sheet closes itself with it). */
  onNavigate?: () => void;
  title?: string;
}

/**
 * Sidebar navigation entry with an active state (`aria-current="page"` comes from NavLink). First-level links
 * pass their `icon`, which leads the label and takes the brand color on the active page instead of the dot.
 */
export function SidebarNavLink({
  end,
  onNavigate,
  indicator,
  icon: Icon,
  children,
  ...props
}: SidebarLinkProps & { end?: boolean; indicator?: boolean; icon?: LucideIcon }) {
  const dot = indicator && !Icon;
  return (
    <NavLink
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          itemClass,
          indicator ? "h-[34px] text-sm" : "h-8 text-[13px]",
          dot && indicatorClass,
          Icon && "group",
          isActive ? "bg-muted font-medium text-foreground" : "text-sidebar-foreground",
          // Only dot links get `before:` classes: a bare one would add an empty flex item and shift the row.
          isActive && dot && "before:bg-brand",
        )
      }
      {...props}
    >
      {({ isActive }) => (
        <>
          {Icon && (
            <Icon
              aria-hidden
              className={cn(
                "size-4! transition-colors",
                isActive ? "text-brand" : "text-muted-foreground group-hover:text-foreground",
              )}
            />
          )}
          {children}
        </>
      )}
    </NavLink>
  );
}

/** Sidebar action link (never shown as active), e.g. "New course". */
export function SidebarActionLink({ onNavigate, ...props }: SidebarLinkProps) {
  return <Link onClick={onNavigate} className={cn(itemClass, "h-8 text-[13px] text-muted-foreground")} {...props} />;
}
